import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api';

/** Social platforms with an icon on the website; their URLs are the admin-managed `social.links` setting. */
export const SOCIAL_PLATFORMS = [
  { key: 'facebook', label: 'Facebook', placeholder: 'https://www.facebook.com/…' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://www.youtube.com/@…' },
  { key: 'instagram', label: 'Instagram', placeholder: 'https://www.instagram.com/…' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://www.linkedin.com/company/…' },
] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number]['key'];

export interface PublicSettings {
  'fees.candidateRegistration.enabled': boolean;
  'fees.candidateRegistration.amountPaise': number;
  'company.profile': {
    legalName: string;
    brandName: string;
    tagline: string;
    phone: string;
    email: string;
    city: string;
    state: string;
    address: string;
    pincode?: string;
    website?: string;
    gstin?: string;
  };
  'social.links'?: Partial<Record<SocialPlatform, string>>;
  'guarantee.days': number;
  'guarantee.enabled': boolean;
  'guarantee.maxReplacements'?: number;
  'guarantee.conditions'?: string[];
  'fees.candidateRegistration.requiredBeforeApply'?: boolean;
  'billing.gstRateBp'?: number;
  'billing.pricesIncludeGst'?: boolean;
  'legal.termsVersion'?: string;
  'legal.privacyVersion'?: string;
  'legal.grievanceOfficer'?: { name: string; designation: string; email: string; phone: string };
  [key: string]: unknown;
}

/** Business values (fees, contact details) come from admin-managed settings, never from code. */
export function usePublicSettings() {
  return useQuery({
    queryKey: ['public-settings'],
    queryFn: async () => (await apiGet<PublicSettings>('/settings/public')).data,
    staleTime: 5 * 60_000,
  });
}
